"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { useToast } from "@/components/ui/toast";
import type { CatalogScope } from "@/lib/host-panel/catalog-types";
import { cn } from "@/lib/utils";

/**
 * Floating mode control (handoff §7.4, criterion 17.18).
 *
 * Both thresholds are literal from the prototype: the lower one keeps the control off the
 * footer, the upper one keeps it from competing with the tab header, where people are still
 * orienting themselves.
 */

/** Handoff §11: how long the confirmation label lasts (`logic/01.js:496`, `_cpT`). */
const COPIED_LABEL_MS = 2400;

/** Handoff §7.4: switches on past this. */
export const MODE_FOB_SHOW_AFTER_PX = 300;

/** Handoff §7.4: switches off within this distance of the document's end. */
export const MODE_FOB_HIDE_NEAR_END_PX = 120;

export function shouldShowModeFob(input: {
  scrollY: number;
  viewportHeight: number;
  documentHeight: number;
}): boolean {
  const distanceToEnd = input.documentHeight - input.viewportHeight - input.scrollY;

  return input.scrollY > MODE_FOB_SHOW_AFTER_PX && distanceToEnd >= MODE_FOB_HIDE_NEAR_END_PX;
}

/**
 * Switches the control on and off as the page scrolls.
 *
 * Listens passively and reads the position in the handler itself: the computation is arithmetic
 * over three numbers, and scheduling a frame for it would cost more than it runs.
 */
function useScrollVisibility(enabled: boolean): boolean {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    function update() {
      setIsVisible(
        shouldShowModeFob({
          scrollY: window.scrollY,
          viewportHeight: window.innerHeight,
          documentHeight: document.documentElement.scrollHeight,
        }),
      );
    }

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [enabled]);

  return enabled && isVisible;
}

/** Catalog v2 (`fobAddLabel`, `mConvLabel`, `copiarLabel`): below this the control speaks short. */
export const MODE_FOB_COMPACT_MAX_WIDTH = 619;

const COMPACT_QUERY = `(max-width: ${MODE_FOB_COMPACT_MAX_WIDTH}px)`;

function subscribeCompact(onChange: () => void) {
  const mediaQuery = window.matchMedia(COMPACT_QUERY);
  mediaQuery.addEventListener("change", onChange);

  return () => mediaQuery.removeEventListener("change", onChange);
}

/**
 * Whether the control takes its short words. Same narrow exception as `use-is-wide-viewport.ts`
 * to the CSS-only presentation rule: the labels are the buttons' accessible names, so hiding one
 * of two spans would still announce both. No hydration risk either: the control only mounts
 * after the page scrolls, never on the server's markup.
 */
function useIsCompact(): boolean {
  return useSyncExternalStore(
    subscribeCompact,
    () => window.matchMedia(COMPACT_QUERY).matches,
    () => false,
  );
}

const MODE_BUTTON_CLASS =
  "inline-flex min-h-[var(--tb-touch-min)] cursor-pointer items-center gap-[7px] whitespace-nowrap rounded-[var(--tb-radius-pill)] py-[11px] text-[13px] leading-[1.3] transition-[background-color,color] duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)]";

export type ModeFobProps = {
  scope: CatalogScope;
  counts: { dentro: number; fora: number };
  onChange: (scope: CatalogScope) => void;
  /** Off while editing: the control must not compete with an open form. */
  enabled?: boolean;
  /**
   * Guest preview (requirement 1.16-1.19, `logic/01.js:188,409-413`): a local, decorative
   * mode, restricted to `dentro`. Optional so pre-existing tests that render `ModeFob` without
   * it still pass; `CatalogSection` always supplies both.
   */
  isGuestPreview?: boolean;
  onToggleGuestPreview?: (next: boolean) => void;
  /** Public list URL for the clipboard action; the action is absent without one. */
  publicListUrl?: string;
  /**
   * Whether the add scope is offered. False for a list that cannot be changed — a closed one
   * (`list-write-access.ts`) — where there is nothing to add.
   */
  canAddPieces?: boolean;
};

export function ModeFob({
  scope,
  counts,
  onChange,
  enabled = true,
  isGuestPreview = false,
  onToggleGuestPreview,
  publicListUrl,
  canAddPieces = true,
}: ModeFobProps) {
  const isVisible = useScrollVisibility(enabled);
  const isCompact = useIsCompact();
  const { showToast } = useToast();
  const [isCopied, setIsCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCopying = useRef(false);

  useEffect(
    () => () => {
      if (resetTimer.current !== null) clearTimeout(resetTimer.current);
    },
    [],
  );

  async function copyLink() {
    if (publicListUrl === undefined) return;
    // A second tap while the first write is still in flight is the same intent, not a new
    // one (criterion 20.6) — without this, a fast double-click writes twice and doubles the
    // toast, since `navigator.clipboard.writeText` carries no reentrancy guard of its own.
    if (isCopying.current) return;
    isCopying.current = true;
    if (resetTimer.current !== null) clearTimeout(resetTimer.current);

    try {
      // Same fallback shape as the panel header's own copy action (`panel-header.tsx`): no
      // `clipboard` outside a secure context, and a rejected `writeText` on denied permission
      // both land here.
      if (navigator.clipboard === undefined) throw new Error("clipboard unavailable");

      await navigator.clipboard.writeText(publicListUrl);

      setIsCopied(true);
      showToast("Link da lista copiado.");
      resetTimer.current = setTimeout(() => setIsCopied(false), COPIED_LABEL_MS);
    } catch {
      setIsCopied(false);
      showToast(
        `Não foi possível copiar automaticamente. Copie o endereço: ${publicListUrl}`,
        { variant: "error" },
      );
    } finally {
      isCopying.current = false;
    }
  }

  if (!isVisible) return null;

  return (
    /*
     * 26px above whatever already occupies the bottom of the panel — the tab bar, on a phone
     * (`--tb-panel-bottom-base`, the prototype's `baseInferior`: `bottom: calc(baseInferior +
     * 26px)`). Outside the panel the variable is unset and this is the plain 26px it always was.
     *
     * And it follows the bar's retreat on the bar's own duration and curve, so the two move as one instead
     * of the control being left floating over a bar that has gone
     * (`03-prototipo-catalogo.template.html:4871`).
     *
     * `--tb-panel-bottom-shift`, never the raw `--tbShift`: the bar publishes that one at every
     * width, so reading it directly pushed this control 64px down on a desktop that has no bar —
     * out of the viewport, rising and dropping with the scroll.
     */
    <div
      className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--tb-panel-bottom-base,0px)+26px)] z-[320] flex translate-y-[var(--tb-panel-bottom-shift,0px)] justify-center px-2 transition-transform duration-[var(--tb-duration-tabbar)] ease-[var(--tb-ease-tabbar)]"
    >
      <div
        role="group"
        aria-label="Controles da sua lista"
        // One row, sized by what it holds. The prototype caps it at 20px from each edge and
        // scrolls the rest sideways (catalog v2, `data-slider`), which with a real catalogue
        // ("Adicionar 732") hid the copy action behind the edge. Here it may grow to 8px from the
        // edges — only a row that needs the room takes it — and the sideways scroll stays as the
        // last resort below 360px.
        className="pointer-events-auto flex max-w-[calc(100vw-16px)] flex-nowrap items-center justify-start gap-[2px] overflow-x-auto rounded-[var(--tb-radius-pill)] bg-[var(--fg)] px-2 py-1.5 [scrollbar-width:none] shadow-[0_20px_48px_rgb(61_63_57_/_0.32)] motion-safe:animate-[tb-flow-in_var(--tb-duration-3)_var(--tb-ease-linen)_both]"
      >
        <ModeButton
          isCompact={isCompact}
          label="Minha lista"
          count={counts.dentro}
          isActive={!isGuestPreview && scope === "dentro"}
          onClick={() => {
            onToggleGuestPreview?.(false);
            onChange("dentro");
          }}
        />
        {canAddPieces ? (
          <ModeButton
            isCompact={isCompact}
            /*
             * **"Explorar peças", não "Adicionar peças"** (conteúdo, validado com a Giu em
             * 2026-10-15).
             *
             * O número ao lado é quanto o catálogo **oferece** — 740 peças para escolher — e com
             * o verbo "adicionar" ele era lido como quanto seria adicionado à lista: a noiva
             * entendia que o clique punha as 740 na lista dela. "Explorar" nomeia o que o botão
             * faz de verdade, que é abrir o catálogo, e deixa o número livre para significar
             * oferta em vez de consequência.
             *
             * O verbo some na versão estreita, não o substantivo: "Explorar" sozinho continua
             * dizendo a ação, enquanto "peças" sozinho não diria nada que o número já não diga.
             */
            label={isCompact ? "Explorar" : "Explorar peças"}
            count={counts.fora}
            isActive={!isGuestPreview && scope === "fora"}
            onClick={() => {
              onToggleGuestPreview?.(false);
              onChange("fora");
            }}
          />
        ) : null}
        {onToggleGuestPreview !== undefined ? (
          <ModeButton
            isCompact={isCompact}
            label={isCompact ? "Prévia" : "Ver como convidado"}
            isActive={isGuestPreview}
            onClick={() => onToggleGuestPreview(!isGuestPreview)}
          />
        ) : null}

        {publicListUrl !== undefined ? (
          <>
            {/* Fio divisor (handoff §7.4, `template.html:734`): 1px, 18px tall. */}
            <span aria-hidden="true" className="mx-1.5 h-[18px] w-px bg-[rgb(245_237_230_/_0.28)]" />
            <button
              type="button"
              onClick={copyLink}
              className={cn(
                MODE_BUTTON_CLASS,
                // The prototype sets this one action apart from the modes: 15px at the sides, 8px
                // to its icon, and never narrower than a touch target when it is the icon alone.
                "min-w-[var(--tb-touch-min)] justify-center gap-2 bg-transparent px-[15px] text-[rgb(245_237_230_/_0.72)] hover:text-tb-white",
              )}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="flex-none"
              >
                <rect x="9" y="9" width="12" height="12" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              {isCompact ? (
                // Icon alone on a phone; the name stays for whoever cannot see it.
                isCopied ? "Copiado" : <span className="sr-only">Copiar link</span>
              ) : isCopied ? (
                "Link copiado"
              ) : (
                "Copiar link"
              )}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function ModeButton({
  label,
  count,
  isActive,
  isCompact,
  onClick,
}: {
  label: string;
  isCompact: boolean;
  count?: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onClick}
      className={cn(
        MODE_BUTTON_CLASS,
        // The prototype's 12px on a phone; 8px under 380px, so a full row still fits a 360px screen.
        isCompact ? "px-3 max-[379px]:px-2" : "px-[18px]",
        isActive
          ? "bg-[var(--tb-porcelain)] text-[var(--fg)]"
          : "bg-transparent text-[rgb(245_237_230_/_0.72)] hover:text-tb-white",
      )}
    >
      {label}
      {count !== undefined ? (
        <span className="font-mono text-[11px] tabular-nums opacity-[0.72]">{count}</span>
      ) : null}
    </button>
  );
}
